import {useMemo} from 'react'
import {useFeed} from '@plebbit/plebbit-react-hooks'
import useBlacklist from './use-blacklist'
import useSetting from './use-setting'

/**
 * Enhanced feed hook that filters out blacklisted content
 * Wraps the original useFeed hook and applies blacklist filtering
 */
const useFilteredFeed = (options) => {
  // Get the original feed data
  const originalFeedData = useFeed(options)
  const {feed, updatedFeed, hasMore, loadMore, ...rest} = originalFeedData

  // Get blacklist functions and user settings
  const {isPostBlacklisted, blacklist} = useBlacklist()
  const [enableBlacklist] = useSetting('enableBlacklist', true)

  // Filter out blacklisted posts from the feed
  const filteredFeed = useMemo(() => {
    if (!feed || !Array.isArray(feed) || !enableBlacklist) {
      return feed
    }

    const filtered = feed.filter((post, index) => {
      const isBlacklisted = isPostBlacklisted(post)

      if (isBlacklisted) {
        console.log('[FilteredFeed] Filtering out blacklisted post:', {
          cid: post.cid,
          author: post.author?.address,
          subplebbit: post.subplebbitAddress,
          title: post.title?.substring?.(0, 50),
        })
      }

      return !isBlacklisted
    })

    // Log filtering statistics
    if (filtered.length !== feed.length) {
      console.log('[FilteredFeed] Filtered feed:', {
        original: feed.length,
        filtered: filtered.length,
        removed: feed.length - filtered.length,
        enabled: enableBlacklist,
      })
    }

    return filtered
  }, [feed, isPostBlacklisted, enableBlacklist])

  // Filter out blacklisted posts from the updatedFeed array as well
  const filteredUpdatedFeed = useMemo(() => {
    if (!updatedFeed || !Array.isArray(updatedFeed) || !feed || !Array.isArray(feed) || !enableBlacklist) {
      return updatedFeed
    }

    // Create a mapping of original indices to filtered indices
    const indexMapping = new Map()
    let filteredIndex = 0

    for (let originalIndex = 0; originalIndex < feed.length; originalIndex++) {
      const post = feed[originalIndex]
      if (!isPostBlacklisted(post)) {
        indexMapping.set(originalIndex, filteredIndex)
        filteredIndex++
      }
    }

    // Build the filtered updatedFeed array
    const filtered = []
    for (let originalIndex = 0; originalIndex < updatedFeed.length; originalIndex++) {
      if (indexMapping.has(originalIndex)) {
        const newIndex = indexMapping.get(originalIndex)
        filtered[newIndex] = updatedFeed[originalIndex]
      }
    }

    return filtered
  }, [updatedFeed, feed, isPostBlacklisted, enableBlacklist])

  // Check if any subplebbits in the options are blacklisted
  const hasBlacklistedSubplebbits = useMemo(() => {
    if (!options.subplebbitAddresses || !Array.isArray(options.subplebbitAddresses)) {
      return false
    }

    return options.subplebbitAddresses.some((address) => blacklist.subplebbits.has(address))
  }, [options.subplebbitAddresses, blacklist.subplebbits])

  // Filter subplebbit addresses to remove blacklisted ones
  const filteredSubplebbitAddresses = useMemo(() => {
    if (!options.subplebbitAddresses || !Array.isArray(options.subplebbitAddresses)) {
      return options.subplebbitAddresses
    }

    const filtered = options.subplebbitAddresses.filter((address) => !blacklist.subplebbits.has(address))

    if (filtered.length !== options.subplebbitAddresses.length) {
      console.log('[FilteredFeed] Filtered blacklisted subplebbits:', {
        original: options.subplebbitAddresses,
        filtered: filtered,
        removed: options.subplebbitAddresses.length - filtered.length,
      })
    }

    return filtered
  }, [options.subplebbitAddresses, blacklist.subplebbits])

  return {
    ...rest,
    feed: filteredFeed,
    updatedFeed: filteredUpdatedFeed,
    hasMore,
    loadMore,
    // Additional information about filtering
    filtering: {
      isBlacklistLoaded: blacklist.lastUpdated > 0,
      blacklistError: blacklist.error,
      hasBlacklistedSubplebbits,
      filteredSubplebbitAddresses,
      blacklistStats: {
        subplebbits: blacklist.subplebbits.size,
        authors: blacklist.authors.size,
        comments: blacklist.comments.size,
      },
    },
  }
}

export default useFilteredFeed
